import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { pipe, filter, map, values as rValues } from 'ramda'
import { useFormikContext } from 'formik'
import { Box, Text, Stack } from '@machobear/design-system'
import { vars } from '@machobear/design-system/css'
import { useGetAddOnsSummary } from '@/hooks/use-promotions'

import { Input } from '@/components/form'
import formatCurrency from '@/utils/format-currency'
import { Item, Product } from '@/features/Promotion/types'

const Link = styled.a`
  color: #4964e9;
  font-weight: ${vars.textWeight.medium};
`

const AddonsTitleWrapper = styled.div`
  margin-top: 16px;
`

const AddonsTitleText = styled.span`
  font-weight: ${vars.textWeight.medium};
`

type requestItem = {
  priceId: string
  quantity: number
}

type Props = {
  data?: any
  setAddOnItems: React.Dispatch<React.SetStateAction<Product>>
  loading?: boolean
  trackingId?: string
  onSubmit?: any
  existingItems: Item[]
  setAddOnsLoading: React.Dispatch<React.SetStateAction<boolean>>
  addOnsLoading: boolean
  couponCode?: string | null | undefined
  hasPaymentMethods: boolean
  paymentMethodState: string
  paymentMethodCountry: string
}

type ApiResponse<T> =
  | { response: Response; body: T }
  | { response: { ok: boolean }; body?: undefined }
  | null

const AddOns = ({
  data,
  setAddOnItems,
  existingItems,
  setAddOnsLoading,
  addOnsLoading,
  couponCode,
  hasPaymentMethods,
  paymentMethodState,
  paymentMethodCountry
}: Props) => {
  const [selectedItems, setSelectedItems] = useState<Item[] | requestItem[]>([])
  const { t } = useTranslation()
  const { values }: { values: { state: string; country: string } } = useFormikContext()
  const getAddOnsSummary = useGetAddOnsSummary()
  const state = hasPaymentMethods ? paymentMethodState : values.state
  const country = hasPaymentMethods ? paymentMethodCountry : values.country
  const formattedCountry = country?.toUpperCase()
  useEffect(() => {
    const fetchData = async () => {
      if (selectedItems?.length > 0) {
        const mappedItems = map(
          item => ({ priceId: item.priceId, quantity: item.quantity }),
          [...existingItems, ...selectedItems]
        )

        try {
          setAddOnsLoading(true)
          const result: ApiResponse<any> = await getAddOnsSummary({
            items: mappedItems,
            couponCode,
            state,
            country: formattedCountry
          })

          if (!result?.response.ok) {
            throw Error(result?.body.message || result?.response.statusText)
          } else {
            setAddOnItems(result?.body)
          }
        } catch (error) {
          console.error('[Promotions/getAddonsSummary]', error)
        }
        setAddOnsLoading(false)
      } else {
        setAddOnItems({
          items: [],
          total_before_tax: '',
          total_after_tax: '',
          total_tax: '',
          total_due: '',
          discount_from_regular_price: '',
          metadata: { package_name: '' }
        })
      }
    }

    fetchData()
  }, [selectedItems, couponCode, state, country])

  // try moving selected items to page level and pass down

  const handleCheck = (item: any, e: any) => {
    const isChecked = e.target.checked
    if (isChecked) {
      // Add the item to the selected items list
      setSelectedItems((prevItems: any) => [...prevItems, item])
    } else {
      // Remove the item from the selected items list
      setSelectedItems((prevItems: any) =>
        prevItems.filter((selectedItem: any) => selectedItem.priceId !== item.priceId)
      )
    }
  }

  const isFrequent = (item: any): boolean => {
    return item.type === 'frequently_bought_together_addon'
  }

  const isSimilar = (item: any): boolean => {
    return item.type === 'similar_products_addon'
  }

  const frequentAddons = pipe(rValues, filter(isFrequent))(data)
  const similarAddons = pipe(rValues, filter(isSimilar))(data)

  return (
    <>
      <Box>
        <Stack space="small">
          {frequentAddons.length > 0 && (
            <>
              <AddonsTitleWrapper>
                <AddonsTitleText>
                  {t('b.promotions.addons.frequently_bought.title', 'Frequently bought together')}
                </AddonsTitleText>
              </AddonsTitleWrapper>
              {frequentAddons.map((item: any) => (
                <Box
                  padding="small"
                  alignItems="center"
                  border="borderNeutralLight"
                  borderRadius="standard"
                  display="flex"
                  key={item.priceId}
                >
                  <Box marginRight="small">
                    <Input
                      onClick={e =>
                        handleCheck({ priceId: item.priceId, quantity: item.quantity }, e)
                      }
                      type="checkbox"
                      disabled={addOnsLoading}
                    />
                  </Box>
                  <Box width="full">
                    <Stack space="xsmall">
                      <Box display="flex" justifyContent="spaceBetween" width="full">
                        <Stack space="xsmall">
                          <Text>{item.name}</Text>
                          <Text tone="secondary" size="small">
                            {`${item.description} `}
                            <Link href={item.metadata.href} target="_blank">
                              {item.metadata.linkText}
                            </Link>
                          </Text>
                        </Stack>
                        <Box marginLeft="small">
                          <Text>{formatCurrency(item.value)}</Text>
                        </Box>
                      </Box>
                    </Stack>
                  </Box>
                </Box>
              ))}
            </>
          )}
          {similarAddons.length > 0 && (
            <>
              <AddonsTitleText>
                {t('b.promotions.addons.similar_products.title', 'Similar products')}
              </AddonsTitleText>
              {similarAddons.map((item: any) => (
                <Box
                  padding="small"
                  alignItems="center"
                  border="borderNeutralLight"
                  borderRadius="standard"
                  display="flex"
                  key={item?.priceId}
                >
                  <Box marginRight="small">
                    <Input
                      onClick={e =>
                        handleCheck({ priceId: item.priceId, quantity: item.quantity }, e)
                      }
                      type="checkbox"
                      disabled={addOnsLoading}
                    />
                  </Box>
                  <Box width="full">
                    <Stack space="xsmall">
                      <Box display="flex" justifyContent="spaceBetween" width="full">
                        <Stack space="xsmall">
                          <Text>{item.name}</Text>
                          <Text tone="secondary" size="small">
                            {`${item.description} `}
                            <Link href={item.metadata.href} target="_blank">
                              {item.metadata.linkText}
                            </Link>
                          </Text>
                        </Stack>
                        <Box marginLeft="small">
                          <Text>{formatCurrency(item.value)}</Text>
                        </Box>
                      </Box>
                    </Stack>
                  </Box>
                </Box>
              ))}
            </>
          )}
        </Stack>
      </Box>
    </>
  )
}

export default AddOns
